import { Alert, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { DomainHealthRecord } from "../../../../domain/healthRecord";
import { localizedDate } from "../../../../utils/dayjs";
import { getFullName } from "../../../../utils/healthRecord";
import LightStyledAutoComplete from "../../../components/mui/LightStyledAutoComplete";
import { useI18n } from "../../../hooks/useI18n";
import { theme } from "../../../layout/Theme";
import { ImportDestinationOption } from "../evcImportDestinations";
import { buildHealthRecordOptionLabel } from "./identity";
import { sectionPaperSx } from "./styles";
import { SummaryField } from "./SummaryField";

export function ExistingHealthRecordSection({
  selectedDestination,
  healthRecordOptions,
  healthRecordOptionsLoading,
  healthRecordOptionsError,
  selectedExistingHealthRecord,
  selectedHealthRecordLoading,
  selectedHealthRecordRequiredError,
  selectedHealthRecordLoadError,
  matchStats,
  hasIdentityMismatch,
  onSelectedHealthRecordChange,
  onSearchChange,
}: {
  selectedDestination: ImportDestinationOption | null;
  healthRecordOptions: DomainHealthRecord[];
  healthRecordOptionsLoading: boolean;
  healthRecordOptionsError: boolean;
  selectedExistingHealthRecord?: DomainHealthRecord;
  selectedHealthRecordLoading: boolean;
  selectedHealthRecordRequiredError: boolean;
  selectedHealthRecordLoadError: boolean;
  matchStats: { exact: number; fresh: number; total: number };
  hasIdentityMismatch: boolean;
  onSelectedHealthRecordChange: (healthRecordId: string | null) => void;
  onSearchChange: (value: string) => void;
}) {
  const { t, locale } = useI18n();
  const selectedHealthRecordDisplayName = selectedExistingHealthRecord
    ? getFullName({
        firstNames: selectedExistingHealthRecord.firstNames,
        lastName: selectedExistingHealthRecord.lastName,
        customaryName: selectedExistingHealthRecord.customaryName,
        gender: selectedExistingHealthRecord.gender,
        t,
      })
    : null;

  return (
    <Paper sx={sectionPaperSx} elevation={0}>
      <Stack spacing={3}>
        <LightStyledAutoComplete<DomainHealthRecord>
          fullWidth
          name="existing-health-record"
          label={t("evc_onboarding.existing_record_label")}
          placeholder={t("evc_onboarding.existing_record_placeholder")}
          value={selectedExistingHealthRecord ?? null}
          options={healthRecordOptions}
          loading={healthRecordOptionsLoading}
          disabled={!selectedDestination}
          getOptionLabel={(option) => buildHealthRecordOptionLabel(option, locale)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => onSelectedHealthRecordChange(value?.id ?? null)}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") {
              onSearchChange(value);
            }
          }}
          error={selectedHealthRecordRequiredError}
          errorMessage={selectedHealthRecordRequiredError ? t("evc_onboarding.existing_record_required") : undefined}
        />
        {healthRecordOptionsError && <Alert severity="error">{t("evc_onboarding.existing_record_lookup_error")}</Alert>}
        {selectedHealthRecordLoadError && (
          <Alert severity="error">{t("evc_onboarding.existing_record_lookup_error")}</Alert>
        )}
        {selectedExistingHealthRecord && (
          <Stack
            spacing={1}
            sx={{
              p: 2,
              border: `solid 1px ${theme.palette.neutral[200]}`,
              borderRadius: 1,
              backgroundColor: theme.palette.neutral[100],
            }}
          >
            <Typography variant="subtitle2" color={theme.palette.neutral[700]}>
              {selectedHealthRecordDisplayName}
            </Typography>
            <SummaryField
              label={t("common.user_infos.birth_date")}
              value={localizedDate(selectedExistingHealthRecord.birthDate, locale)}
            />
            <Typography variant="body2" color={theme.palette.neutral[600]}>
              {t("evc_onboarding.existing_record_summary", {
                exact: matchStats.exact,
                new: matchStats.fresh,
                total: matchStats.total,
              })}
            </Typography>
            {selectedHealthRecordLoading && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} />
                <Typography variant="body2">{t("evc_onboarding.existing_record_loading")}</Typography>
              </Stack>
            )}
            {hasIdentityMismatch && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t("evc_onboarding.existing_record_identity_warning")}
              </Alert>
            )}
          </Stack>
        )}
        {selectedHealthRecordRequiredError && !selectedExistingHealthRecord && (
          <Alert severity="warning">{t("evc_onboarding.existing_record_required")}</Alert>
        )}
      </Stack>
    </Paper>
  );
}
